<template>
  <div>
    <div :class="`wrapper-page${isMenuOpen ? '' : '-closeSidebar'}`">
      <!-- Mobile -->
      <PageSidebarMobile :show="isMenuOpen && currentDevice != 'desktop'" />

      <!-- Desktop -->
      <page-sidebar
        title="TRAVELCAB"
        :isMenuOpen="isMenuOpen"
        :tooltipType="tooltipType"
        @toggleMenu="toggleMenu"
        v-if="currentDevice == 'desktop'"
      >

        <template #custom-header>
          <img
            src="@/core/assets/images/layout/logo-admin-transparent.png"
            alt="Razzil"
            class="sidebar-logo"
          />
          <span class="sidebar-title w-50">TRAVELCAB</span>
        </template>

        <template #list-items>
          <!-- === AFFILIATE MENU === -->
          <template v-if="isAffiliate">
            <router-link
              :to="{ name: 'AffiliateDashboard' }"
              tag="li"
              exact-active-class="active"
              class="d-flex sidebar-item"
            >
              <div class="d-flex align-items-center">
                <span class="menu-icon"><i class="ri-dashboard-line"></i></span>
                <span class="sidebar-item-text">Dashboard</span>
              </div>
            </router-link>

            <router-link
              :to="{ name: 'AffiliateRides' }"
              tag="li"
              exact-active-class="active"
              class="d-flex sidebar-item"
            >
              <div class="d-flex align-items-center">
                <span class="menu-icon"><i class="ri-car-line"></i></span>
                <span class="sidebar-item-text">Mis Viajes</span>
              </div>
            </router-link>

            <router-link
              :to="{ name: 'AffiliatePassword' }"
              tag="li"
              exact-active-class="active"
              class="d-flex sidebar-item"
            >
              <div class="d-flex align-items-center">
                <span class="menu-icon"><i class="ri-lock-line"></i></span>
                <span class="sidebar-item-text">Cambiar contrasena</span>
              </div>
            </router-link>
          </template>

          <!-- === ADMIN MENU (data-driven, dos niveles) === -->
          <template v-else>
            <template v-for="item in menuItems">
              <!-- Ítem simple -->
              <router-link
                v-if="!item.children"
                :key="item.key"
                :to="{ name: item.route }"
                tag="li"
                active-class="active"
                class="d-flex sidebar-item"
                @mouseover.native="tooltipType = item.key"
                @mouseleave.native="tooltipType = null"
              >
                <div class="d-flex align-items-center">
                  <span class="menu-icon">
                    <i :class="item.icon"></i>
                  </span>
                  <span class="sidebar-item-text">{{ item.text }}</span>
                  <CustomTooltip
                    custom-class="tooltip-sidebar"
                    v-if="!isMenuOpen && tooltipType === item.key"
                    :text="item.text"
                  />
                </div>
              </router-link>

              <!-- Grupo con submenú -->
              <li
                v-else
                :key="item.key"
                class="sidebar-item d-flex align-items-center"
                :class="{ active: isActiveGroup(item) }"
                @click.prevent="setCurrentSubmenu(item.key)"
              >
                <div class="d-flex flex-column justify-content-center sidebar-item-container">
                  <div class="d-flex align-items-center justify-content-between w-100">
                    <div
                      class="d-flex"
                      @mouseover="tooltipType = item.key"
                      @mouseleave="tooltipType = null"
                    >
                      <span class="menu-icon">
                        <i :class="item.icon"></i>
                      </span>
                      <span class="sidebar-item-text">{{ item.text }}</span>
                      <CustomTooltip
                        custom-class="tooltip-sidebar"
                        v-if="!isMenuOpen && tooltipType === item.key"
                        :text="item.text"
                      />
                    </div>
                  </div>
                  <span
                    v-if="item.description"
                    :class="`sidebar-item-description ${isActiveGroup(item) ? 'active' : ''}`"
                  >
                    {{ item.description }}
                  </span>
                </div>
                <div v-if="isMenuOpen">
                  <span v-if="isOpen(item)">
                    <i class="ri-arrow-up-s-line"></i>
                  </span>
                  <span v-else>
                    <i class="ri-arrow-down-s-line"></i>
                  </span>
                </div>
              </li>

              <!-- Submenú del grupo -->
              <section v-if="item.children" :key="`${item.key}-submenu`">
                <ul :class="`sidebar-submenu ${isOpen(item) ? 'active' : ''} pl-0`">
                  <sub-item
                    v-for="child in item.children"
                    :key="child.route"
                    :isMenuOpen="isMenuOpen"
                    :routeName="child.route"
                    :text="child.text"
                  ></sub-item>
                </ul>
              </section>
            </template>
          </template><!-- /v-else admin menu -->
        </template>
      </page-sidebar>

      <div class="wrapper-page__content">
        <page-header />
        <div class="wrapper-page__inner">
          <b-container fluid>
            <!-- La key cambia con la ciudad (o al pasar a Administración) y
                 obliga a remontar la página: vuelve a pedir sus datos con el
                 nuevo X-City-Id y descarta lo cacheado de la ciudad anterior.
                 Sin esto, cambiar de ciudad estando en la misma ruta no
                 recargaba nada. El header y el sidebar quedan afuera. -->
            <div :key="levelKey">
              <slot></slot>
            </div>
          </b-container>
        </div>
      </div>

      <page-footer />
    </div>
  </div>
</template>

<script>
import PageHeader from "./PageHeader";
import PageSidebarMobile from "./layout/PageSidebarMobile";

import {
  PageSidebar,
  CustomTooltip,
  PageFooter,
  SubItem
} from "wize-admin";

import { mapGetters, mapMutations } from "vuex";
import { ADMIN_MENU, CITY_MENU } from "@/core/utils/menus";

export default {
  name: "PageBase",
  components: {
    PageHeader,
    PageSidebarMobile,
    PageSidebar,
    PageFooter,
    CustomTooltip,
    SubItem
  },
  data() {
    return {
      matchMediaMd: window.matchMedia("(max-width: 768px)"),
      tooltipType: null,
      currentDevice: "desktop",
      lastWidth: window.innerWidth,
    };
  },
  computed: {
    ...mapGetters({
      currentSubmenu: "app/getCurrentSubmenu",
      isMenuOpen: "app/isMenuOpen",
      isAffiliate: "auth/isAffiliate",
      isAdminLevel: "city/isAdminLevel",
      currentCity: "city/getCurrentCity"
    }),
    levelKey() {
      if (this.isAdminLevel) return "admin";
      return this.currentCity ? `city-${this.currentCity.id}` : "none";
    },
    // Nivel 1 (Administración) vs Nivel 2 (ciudad activa) — DEF-ADM-009
    menuItems() {
      return this.isAdminLevel ? ADMIN_MENU : CITY_MENU;
    }
  },
  methods: {
    calcResize() {
      this.setCurrentDevice();

      this.lastWidth = window.innerWidth;

      window.addEventListener("resize", () => {
        const currentWidth = window.innerWidth;

        if (currentWidth !== this.lastWidth) {
          this.setCurrentDevice();
          this.lastWidth = currentWidth;
        }
      });
    },
    setCurrentDevice() {
      if (screen.width < 992) {
        this.currentDevice = "mobile";
        this.setMenu(false);
      } else {
        this.currentDevice = "desktop";
        this.setMenu(true);
      }
    },
    isMatchMedia() {
      if (this.matchMediaMd.matches) {
        this.setMenu(false);
      } else {
        this.setMenu(true);
      }
    },
    // Desplegado: el operador abrió el grupo, o el grupo contiene la pantalla
    // actual (así el submenú de la sección en la que estás siempre se ve).
    isCurrent(submenu) {
      return this.currentSubmenu.includes(submenu);
    },
    isOpen(item) {
      return this.isCurrent(item.key) || this.isActiveGroup(item);
    },
    // Activo: solo el grupo al que pertenece la ruta actual. Antes se usaba el
    // estado de desplegado para resaltar, así que cada grupo que se hubiera
    // abierto alguna vez quedaba marcado como activo (se guarda en
    // localStorage) y se veían varios a la vez.
    isActiveGroup(item) {
      if (!item.children) return false;
      return item.children.some((child) => this.isChildRoute(child.route));
    },
    // Compara por ruta y no por nombre para que una pantalla anidada (por
    // ejemplo /administracion/importar/despegar/cargar) siga marcando su
    // sección.
    isChildRoute(routeName) {
      const resolved = this.$router.resolve({ name: routeName });
      const path = resolved && resolved.route ? resolved.route.path : null;
      if (!path) return false;
      return this.$route.path === path || this.$route.path.startsWith(`${path}/`);
    },
    ...mapMutations({
      setCurrentSubmenu: "app/setCurrentSubmenu",
      setMenu: "app/setMenu",
      toggleMenu: "app/toggleMenu",
    }),
  },
  created() {
    this.matchMediaMd.addListener(this.isMatchMedia);
  },
  async mounted() {
    this.calcResize();
  },
};
</script>

<style scoped lang="stylus">
.sidebar-logo {
  max-width: 25px;
}
</style>
